/**
 * Service-worker registration for SNOW Web PWA.
 *
 * Scope: SNOW Web hosts only (platform.* and summarisenow.*). Patient
 * hosts (*.summary.to) explicitly UNREGISTER any SW on load — caching shell
 * assets there caused stale-chunk + MIME errors. The patient form is plain
 * SPA navigation; no offline value.
 *
 * Updates: when a new version is deployed, the existing SW will fetch the new
 * sw.js, install it, and call skipWaiting(). Vite's hashed filenames mean
 * stale chunks fall back to network and get re-cached.
 */
const HOST = typeof window !== "undefined" ? window.location.hostname : ""
const isPatientHost = HOST.endsWith(".summary.to") || HOST === "summary.to"

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return

  // Patient hosts: nuke any existing SW + caches and bail.
  if (isPatientHost) {
    void unregisterAll()
    return
  }

  if (!import.meta.env.PROD) return

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Periodic update check — every 30 min while a tab is open. Cheap
        // and ensures clinic users on always-open tabs pick up the latest
        // build without manual reload.
        setInterval(() => { reg.update().catch(() => {}) }, 30 * 60 * 1000)
      })
      .catch((err) => {
        console.warn("[sw] registration failed:", err)
      })
  })
}

/**
 * Aggressive cleanup — unregisters every SW + clears every cache. Used on
 * patient hosts and exposed for support: append `?reset-sw=1` to any URL.
 */
async function unregisterAll() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    await Promise.all(regs.map((r) => r.unregister()))
    if ("caches" in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch (err) {
    console.warn("[sw] cleanup failed:", err)
  }
}

// Support reset URL: anyone hitting `?reset-sw=1` wipes their SW + caches.
if (typeof window !== "undefined" && window.location.search.includes("reset-sw=1")) {
  void unregisterAll().then(() => {
    // Strip the param and reload so the page comes back clean.
    const u = new URL(window.location.href)
    u.searchParams.delete("reset-sw")
    window.location.replace(u.toString())
  })
}
